/**
 * ChildSchoolYearsPage — 5a from the design handoff.
 *
 * Route: /my-life/child/:memberId/school-years
 * Two views: "School years" (default) and "Activities".
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Backpack, CalendarRange, Trophy, Medal, Tent, Users as UsersIcon,
  Plus, ChevronDown, ChevronUp, ChevronRight, Image, Film,
} from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import { getSchoolYears, getActivities } from '../services/schoolYearsAPI';
import { gradeLabel, gradeLabelShort, schoolYearRange, stageForGrade } from '../utils/schoolYears';
import { getMediaServeUrl } from '../config/mediaAPI';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

function resolveThumb(m) {
  if (!m) return '';
  if (m.file_url?.startsWith('http')) return m.file_url;
  if (m.url?.startsWith('http')) return m.url;
  if (m.url?.startsWith('/')) return `${API_ORIGIN}${m.url}`;
  if (m.id) return getMediaServeUrl(m.id);
  return '';
}

const CATEGORY_ICONS = {
  club: UsersIcon,
  scouts: Tent,
  team_sport: Trophy,
  individual_sport: Medal,
};

const CATEGORY_FILTERS = [
  { key: 'all', label: 'Everything', icon: null },
  { key: 'club', label: 'Clubs', icon: UsersIcon },
  { key: 'scouts', label: 'Scouts', icon: Tent },
  { key: 'team_sport', label: 'Team sports', icon: Trophy },
  { key: 'individual_sport', label: 'Individual sports', icon: Medal },
];

// ─── Grade Card ───
function GradeCard({ grade, isCurrent, isExpanded, onToggle, categoryFilter }) {
  const hasStories = grade.storyCount > 0;
  const filteredActivities = categoryFilter === 'all'
    ? grade.activities
    : grade.activities; // filtering would need activity category data

  return (
    <div style={{
      border: isCurrent ? '2px solid #3E4F22' : '1.5px solid #D6D1BD',
      borderRadius: 14, background: '#fff', marginBottom: 8,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <button
        onClick={onToggle}
        style={{
          width: '100%', border: 'none', cursor: 'pointer',
          background: isCurrent && isExpanded ? '#F1F5E8' : '#fff',
          padding: '14px 16px',
          display: 'flex', alignItems: 'center', gap: 12,
          minHeight: 64,
        }}
      >
        {/* Grade circle */}
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: isCurrent ? '#3E4F22' : '#F1F5E8',
          border: isCurrent ? 'none' : '2px solid #3E4F22',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: FONT_B, fontSize: 16, fontWeight: 700,
          color: isCurrent ? '#fff' : '#3E4F22',
        }}>
          {gradeLabelShort(grade.grade)}
        </div>

        {/* Text */}
        <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontFamily: FONT_B, fontSize: 16.5, fontWeight: 700, color: '#2C2F26',
            }}>
              {gradeLabel(grade.grade)}
            </span>
            {isCurrent && (
              <span style={{
                fontFamily: FONT_B, fontSize: 12, fontWeight: 700, color: '#3E4F22',
                background: '#F1F5E8', borderRadius: 6, padding: '2px 8px',
              }}>
                This year
              </span>
            )}
          </div>
          <p style={{
            fontFamily: FONT_B, fontSize: 14, color: '#4A4F40', margin: '2px 0 0',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {schoolYearRange(grade.schoolYear)}
            {grade.teacher && ` \u00B7 ${grade.teacher}`}
            {grade.storyCount > 0 && ` \u00B7 ${grade.storyCount} ${grade.storyCount === 1 ? 'story' : 'stories'}`}
          </p>
        </div>

        {/* Story count + chevron */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {!isExpanded && grade.storyCount > 0 && (
            <span style={{ fontFamily: FONT_B, fontSize: 14, color: '#4A4F40' }}>
              {grade.storyCount} {grade.storyCount === 1 ? 'story' : 'stories'}
            </span>
          )}
          {isExpanded ? <ChevronUp size={20} color="#7F846F" /> : <ChevronDown size={20} color="#7F846F" />}
        </div>
      </button>

      {/* Expanded body */}
      {isExpanded && (
        <div style={{ padding: '0 16px 16px' }}>
          {/* Activities */}
          {filteredActivities?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <p style={{
                fontFamily: FONT_B, fontSize: 12.5, fontWeight: 700,
                color: '#4A4F40', letterSpacing: '.04em', margin: '0 0 8px',
                textTransform: 'uppercase',
              }}>
                THIS YEAR
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {filteredActivities.map((a, i) => (
                  <span key={i} style={{
                    fontFamily: FONT_B, fontSize: 14.5, fontWeight: 600,
                    color: '#3E4F22', background: '#F1F5E8', borderRadius: 10,
                    padding: '8px 14px', minHeight: 44,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}>
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Stories — thumbnail cards matching ChapterStation */}
          {grade.stories?.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {grade.stories.map(s => {
                const thumbUrl = s.thumbnail_url
                  ? (s.thumbnail_url.startsWith('http') ? s.thumbnail_url : `${API_ORIGIN}${s.thumbnail_url}`)
                  : null;
                return (
                  <a key={s.id} href={`/stories/${s.id}`}
                    onClick={e => { e.preventDefault(); window.location.href = `/stories/${s.id}`; }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14, padding: 12,
                      border: '1.5px solid #E4E1D2', borderRadius: 12, textDecoration: 'none', color: 'inherit',
                    }}
                  >
                    <div style={{
                      width: 92, height: 68, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
                      background: '#EFEDE3', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {thumbUrl ? (
                        <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                      ) : (
                        <Image size={24} color="#3F4436" />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 17, color: '#2C2F26', fontFamily: FONT_B }}>{s.title}</div>
                      <div style={{ fontSize: 14.5, color: '#4A4F40', fontFamily: FONT_B }}>{s.year || s.date || ''}</div>
                    </div>
                    <ChevronRight size={22} color="#3F4436" style={{ flexShrink: 0 }} />
                  </a>
                );
              })}
              {grade.storyCount > 2 && (
                <button style={{
                  background: 'none', border: 'none', color: '#3E4F22', fontSize: 15,
                  fontWeight: 700, cursor: 'pointer', fontFamily: FONT_B,
                  textDecoration: 'underline', textUnderlineOffset: '3px',
                  padding: '10px 2px', textAlign: 'left',
                }}>
                  See all {grade.storyCount} stories from {gradeLabel(grade.grade)}
                </button>
              )}
            </div>
          )}

          {!hasStories && (
            <p style={{ fontFamily: FONT_B, fontSize: 15, color: '#7F846F', margin: 0 }}>
              No stories yet for this grade.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Stage Group ───
function StageGroup({ stage }) {
  const [expanded, setExpanded] = useState(new Set(
    stage.grades.filter(g => g.isCurrent).map(g => g.grade)
  ));

  const toggle = (g) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(g) ? next.delete(g) : next.add(g);
    return next;
  });

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Stage header */}
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: 10, padding: '0 4px',
      }}>
        <div>
          <span style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 20, color: '#2C2F26' }}>
            {stage.name}
          </span>
          {stage.schoolName && (
            <span style={{ fontFamily: FONT_B, fontSize: 15, fontWeight: 600, color: '#4A4F40', marginLeft: 10 }}>
              {stage.schoolName}
            </span>
          )}
        </div>
        <span style={{ fontFamily: FONT_B, fontSize: 14, color: '#4A4F40' }}>
          {stage.totalStories} {stage.totalStories === 1 ? 'story' : 'stories'}
        </span>
      </div>

      {/* Grade cards */}
      {stage.grades.map(g => (
        <GradeCard
          key={g.grade}
          grade={g}
          isCurrent={g.isCurrent}
          isExpanded={expanded.has(g.grade)}
          onToggle={() => toggle(g.grade)}
          categoryFilter="all"
        />
      ))}

      {/* Future grades */}
      {stage.futureGrades?.length > 0 && (
        <div style={{
          border: '2px dashed #D6D1BD', borderRadius: 14,
          padding: '14px 16px', marginTop: 4,
          fontFamily: FONT_B, fontSize: 15, color: '#7F846F',
        }}>
          {stage.futureGrades.map(g => gradeLabel(g)).join(', ')} will open as each school year starts.
        </div>
      )}
    </div>
  );
}

// ─── Before School Section ───
function BeforeSchool({ data }) {
  if (!data) return null;
  const sections = [
    { key: 'babyToddler', label: 'Baby & toddler', count: data.babyToddler?.storyCount || 0 },
    { key: 'preschool', label: 'Preschool', count: data.preschool?.storyCount || 0 },
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 20, color: '#2C2F26', marginBottom: 10 }}>
        Before school
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {sections.map(s => (
          <div key={s.key} style={{
            border: s.count > 0 ? '1.5px solid #D6D1BD' : '2px dashed #D6D1BD',
            borderRadius: 14, padding: '16px 14px',
            background: '#fff', textAlign: 'center',
          }}>
            <p style={{ fontFamily: FONT_B, fontSize: 16, fontWeight: 700, color: '#2C2F26', margin: '0 0 4px' }}>
              {s.label}
            </p>
            {s.count > 0 ? (
              <p style={{ fontFamily: FONT_B, fontSize: 14, color: '#4A4F40', margin: 0 }}>
                {s.count} {s.count === 1 ? 'story' : 'stories'}
              </p>
            ) : (
              <button style={{
                fontFamily: FONT_B, fontSize: 14.5, fontWeight: 700,
                color: '#3E4F22', background: 'none', border: 'none',
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                <Plus size={14} /> Add
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Activities View ───
function ActivitiesView({ activities, personName }) {
  const [selectedId, setSelectedId] = useState(null);
  const selected = activities?.find(a => a.id === selectedId);

  if (!activities?.length) {
    return (
      <div style={{
        background: '#fff', border: '1.5px solid #D6D1BD', borderRadius: 16,
        padding: 32, textAlign: 'center',
      }}>
        <p style={{ fontFamily: FONT_B, fontSize: 16, color: '#4A4F40', marginBottom: 16 }}>
          No activities yet.
        </p>
        <button style={{
          fontFamily: FONT_B, fontSize: 16, fontWeight: 700, color: '#3E4F22',
          background: 'none', border: 'none', cursor: 'pointer',
          textDecoration: 'underline', textUnderlineOffset: 3,
        }}>
          <Plus size={14} style={{ marginRight: 4 }} />
          Add an activity
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Activity list */}
      {activities.map(act => {
        const Icon = CATEGORY_ICONS[act.category] || Trophy;
        const isSelected = act.id === selectedId;
        return (
          <div key={act.id} style={{ marginBottom: isSelected ? 12 : 8 }}>
            <button
              onClick={() => setSelectedId(isSelected ? null : act.id)}
              style={{
                width: '100%', border: isSelected ? '2px solid #E65100' : '1.5px solid #D6D1BD',
                borderRadius: 14, background: '#fff', cursor: 'pointer',
                padding: '14px 16px',
                display: 'flex', alignItems: 'center', gap: 12,
                minHeight: 58,
              }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                background: isSelected ? '#E65100' : '#F1F5E8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={20} color={isSelected ? '#fff' : '#3E4F22'} />
              </div>
              <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                <p style={{
                  fontFamily: FONT_H, fontWeight: 700, fontSize: 17, color: '#2C2F26', margin: 0,
                }}>
                  {act.name}
                </p>
                <p style={{ fontFamily: FONT_B, fontSize: 14, color: '#4A4F40', margin: '2px 0 0' }}>
                  {act.category.replace('_', ' ')}
                  {act.yearRange && ` \u00B7 ${act.yearRange}`}
                  {act.storyCount > 0 && ` \u00B7 ${act.storyCount} ${act.storyCount === 1 ? 'story' : 'stories'}`}
                </p>
              </div>
              {isSelected
                ? <ChevronUp size={20} color="#E65100" />
                : <ChevronDown size={20} color="#7F846F" />}
            </button>

            {/* Detail card — story thumbnails */}
            {isSelected && selected && (
              <div style={{
                border: '2px solid #E65100', borderRadius: 16,
                background: '#fff', marginTop: -2, overflow: 'hidden',
              }}>
                <div style={{ background: '#FFF8F1', padding: '14px 16px' }}>
                  <p style={{ fontFamily: FONT_B, fontSize: 15, color: '#4A4F40', margin: 0 }}>
                    {selected.category.replace('_', ' ')}
                    {` \u00B7 ${selected.seasonCount} ${selected.seasonCount === 1 ? 'season' : 'seasons'}`}
                    {selected.storyCount > 0 && ` \u00B7 ${selected.storyCount} ${selected.storyCount === 1 ? 'story' : 'stories'}`}
                  </p>
                </div>
                <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(selected.previews || []).map(s => {
                    const thumbUrl = s.thumbnail_url
                      ? (s.thumbnail_url.startsWith('http') ? s.thumbnail_url : `${API_ORIGIN}${s.thumbnail_url}`)
                      : null;
                    return (
                      <a key={s.id} href={`/stories/${s.id}`}
                        onClick={e => { e.preventDefault(); window.location.href = `/stories/${s.id}`; }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 14, padding: 12,
                          border: '1.5px solid #E4E1D2', borderRadius: 12, textDecoration: 'none', color: 'inherit',
                        }}
                      >
                        <div style={{
                          width: 92, height: 68, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
                          background: '#EFEDE3', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {thumbUrl ? (
                            <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                          ) : (
                            <Image size={24} color="#3F4436" />
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 17, color: '#2C2F26', fontFamily: FONT_B }}>{s.title}</div>
                          <div style={{ fontSize: 14.5, color: '#4A4F40', fontFamily: FONT_B }}>{s.year || s.date || ''}</div>
                        </div>
                        <ChevronRight size={22} color="#3F4436" style={{ flexShrink: 0 }} />
                      </a>
                    );
                  })}
                  {selected.storyCount > 2 && (
                    <button style={{
                      background: 'none', border: 'none', color: '#E65100', fontSize: 15,
                      fontWeight: 700, cursor: 'pointer', fontFamily: FONT_B,
                      textDecoration: 'underline', textUnderlineOffset: '3px',
                      padding: '10px 2px', textAlign: 'left',
                    }}>
                      See all {selected.storyCount} stories
                    </button>
                  )}
                  {(!selected.previews || selected.previews.length === 0) && (
                    <p style={{ fontFamily: FONT_B, fontSize: 14.5, color: '#7F846F', margin: 0 }}>
                      No stories yet for this activity.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════
export default function ChildSchoolYearsPage() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') || 'years';

  const [data, setData] = useState(null);
  const [activitiesData, setActivitiesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sy, acts] = await Promise.all([
        getSchoolYears(memberId),
        getActivities(memberId),
      ]);
      setData(sy);
      setActivitiesData(acts);
    } catch (err) {
      console.error('Failed to load school years:', err);
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { loadData(); }, [loadData]);

  const switchView = (v) => {
    setSearchParams({ view: v });
  };

  if (loading) {
    return (
      <HubLayout>
        <div style={{ padding: 40, textAlign: 'center' }}>
          <p style={{ fontFamily: FONT_B, fontSize: 16, color: '#7F846F' }}>Loading...</p>
        </div>
      </HubLayout>
    );
  }

  if (!data) {
    return (
      <HubLayout>
        <div style={{ padding: 40, textAlign: 'center' }}>
          <p style={{ fontFamily: FONT_B, fontSize: 16, color: '#7F846F' }}>No school year data available.</p>
        </div>
      </HubLayout>
    );
  }

  const person = data.person;
  const currentGrade = data.currentGrade;

  return (
    <HubLayout>
      <div style={{
        background: '#F5F5DC', borderRadius: 18, padding: '28px 30px',
        maxWidth: 900, margin: '0 auto',
      }}>
        {/* Header row */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
          marginBottom: 24,
        }}>
          {/* Icon tile */}
          <div style={{
            width: 44, height: 44, borderRadius: 12, background: '#6E531A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Backpack size={22} color="#fff" />
          </div>

          {/* Title */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 style={{
              fontFamily: FONT_H, fontWeight: 700, fontSize: 24, color: '#2C2F26',
              margin: 0, lineHeight: 1.1,
            }}>
              {person.firstName} growing up
            </h1>
            <p style={{
              fontFamily: FONT_B, fontSize: 14.5, color: '#4A4F40', margin: '3px 0 0',
            }}>
              {person.birthYear && `Born ${person.birthYear}`}
              {currentGrade != null && ` \u00B7 now in ${gradeLabel(currentGrade)}`}
              {data.totalStories > 0 && ` \u00B7 ${data.totalStories} ${data.totalStories === 1 ? 'story' : 'stories'}`}
            </p>
          </div>

          {/* Segmented tabs */}
          <div style={{
            background: '#EDE8CD', borderRadius: 10, padding: 4,
            display: 'flex', gap: 2,
          }}>
            {[
              { key: 'years', label: 'School years', icon: CalendarRange },
              { key: 'activities', label: 'Activities', icon: Trophy },
            ].map(tab => {
              const active = view === tab.key;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => switchView(tab.key)}
                  role="tab"
                  aria-selected={active}
                  style={{
                    minHeight: 44, padding: '0 18px', borderRadius: 8,
                    border: 'none', cursor: 'pointer',
                    background: active ? '#fff' : 'transparent',
                    boxShadow: active ? '0 1px 2px rgba(54,60,40,.15)' : 'none',
                    fontFamily: FONT_B, fontSize: 15.5, fontWeight: 700,
                    color: active ? '#556B2F' : '#3F4436',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Add memory button */}
          <button
            onClick={() => navigate('/board')}
            style={{
              minHeight: 48, padding: '0 20px', borderRadius: 12,
              background: '#3E4F22', border: 'none',
              fontFamily: FONT_B, fontSize: 16, fontWeight: 700, color: '#fff',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <Plus size={18} /> Add a memory
          </button>
        </div>

        {/* School years view */}
        {view === 'years' && (
          <>
            {/* Filter chips */}
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20,
            }}>
              {CATEGORY_FILTERS.map(f => {
                const active = categoryFilter === f.key;
                const Icon = f.icon;
                return (
                  <button
                    key={f.key}
                    onClick={() => setCategoryFilter(f.key)}
                    style={{
                      minHeight: 44, padding: '0 16px', borderRadius: 100,
                      border: active ? 'none' : '1.5px solid #9A9F8A',
                      background: active ? '#3E4F22' : '#fff',
                      fontFamily: FONT_B, fontSize: 15, fontWeight: 700,
                      color: active ? '#fff' : '#3F4436',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    {Icon && <Icon size={16} />}
                    {f.label}
                  </button>
                );
              })}
            </div>

            {/* Stage groups */}
            {data.stages?.map(stage => (
              <StageGroup key={stage.name} stage={stage} />
            ))}

            {/* Before school */}
            <BeforeSchool data={data.beforeSchool} />
          </>
        )}

        {/* Activities view */}
        {view === 'activities' && (
          <ActivitiesView
            activities={activitiesData?.activities || []}
            personName={person.firstName}
          />
        )}
      </div>

      {/* Responsive overrides */}
      <style>{`
        @media (max-width: 640px) {
          [style*="padding: 28px 30px"] { padding: 16px 14px !important; }
        }
      `}</style>
    </HubLayout>
  );
}
